/* eslint-disable no-undef, no-unused-vars */
import { Tuple } from "./types";
import { ConfigApi } from "./base/ConfigApi";
import SpaToolsApiClient from "./base/SpaToolsApiClient";

export type AccessEmployee = {
  employeeID: string;
  employeeKey: string;
  employeeName: string;
  isCanApprove: boolean;
};

export type CommunicationType = {
  comTypeId: number;
  comTypeName: string;
};

type JobRecipient = {
  mailingJobRecipientId: number;
  mailingJobId: number;
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipcode: number;
  emailAddress: string;
  printedSent: boolean;
  emailSent: boolean;
  isCustomAddress: boolean | null;
  customAddressLine1: string | null;
  customAddressLine2: string | null;
  customCity: string | null;
  customState: string | null;
  customZipCode: number | null;
  mailingJob: any;
};

export type JobItem = {
  mailingJobId: number;
  comTypeId: number;
  employeeKey: number;
  companyKey: number;
  dateCreated: string;
  dateApproved: string | null;
  dateSentToPrintHouse: string | null;
  deliveryMethodId: number | null;
  statusId: number;
  propertyName: string | null;
  employeeName: string | null;
  pageCount: number | null;
  inputFileBase64: string | null;
  comType: any;
  deliveryMethod: any;
  jobApprovals: JobApproval[];
  mailingJobOptions: any[];
  mailingJobRecipients: JobRecipient[];
  status: any;
};

export type JobApproval = {
  jobApprovalId: number;
  mailingJobId: number;
  submitterEmployeeKey: number;
  approverEmployeeKey: number;
  dateSubmittedApproval: string;
  dateApprovedDenied: string | null;
  isApproved: boolean;
  mailingJob: any | null;
};

export type Property = {
  id: number;
  name: string;
  titleInDropdown?: string;
};

export type ManagmentCompany = {
  mcId: number;
  mcEin: string;
  companyName: string;
};

export type PrintColorType = {
  printColorId: number;
  printColor1: string;
  comTypePrintColorMtms: any[];
  mailingJobOptions: any[];
};

export type CommunicationTypeHelpArea = {
  helpAreaId: number;
  helpAreaText: string;
  helpState: string;
  communicationTypes: string[];
  deliveryMethods: string[];
  sendOptions: any[];
};

export type DeliveryMethod = {
  id: number;
  methodName: string;
  dm_Active: number;
  deliveryMethodXml: string;
};

export type PrintColorMethod = {
  id: number;
  optionName: string;
  pc_Active: number;
};

export type PrintPreference = {
  id: number;
  optionName: string;
  pp_Active: number;
};

export type SendOption = {
  id: number;
  optionName: string;
  so_Active: number;
};

export type ScopeDescription = {
  comTypeId: number;
  scopeDescription: string;
};

export type EmailDefault = {
  emailDefaultId: number;
  comTypeId: number;
  defaultSubject: string;
  defaultEmailBody: string;
  senderName: string;
  senderEmail: string;
  replyEmail: string;
  emailSubject: string;
  comType: any;
};

export type CommunityForProperty = {
  companyKey: number;
  c_Name: string;
  communityKey: number;
  co_Name: string;
  residentUnits: number;
  co_TaxID: string;
  co_CouponCode: string;
};

export type GetCommunityData = {
  communityId?: number[];
  propertyId: number;
  communicationId: string;
};

export default class Api_v1 extends SpaToolsApiClient {
  constructor(configApi: ConfigApi) {
    super({ ...configApi, domain: configApi.domain + "v1/" });
  }

  public async getToken(): Promise<string> {
    return await this.get("/Recipients/GetToken")
      .then((res) => res.data)
      .catch(() => undefined);
  }

  async getPropertyListAsync(): Promise<Property[]> {
    return await this.get(`/Recipients/GetPropertyList`)
      .then((res) => res.data.properties)
      .catch(() => []);
  }

  public async getCommunicationTypeList(): Promise<CommunicationType[]> {
    return await this.get("/Recipients/GetCommunicationTypeList")
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getCommunicationTypeId(id: string): Promise<any> {
    return await this.get("/Recipients/GetCommunicationTypeId?comId=" + id)
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getManagmentCompany(): Promise<ManagmentCompany[]> {
    return await this.get("/Recipients/GetManagmentCompany")
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getPrintColorTypeList(): Promise<PrintColorType[]> {
    return await this.get("/Recipients/GetPrintColorTypeList")
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getAccessEmployee(): Promise<AccessEmployee> {
    return await this.get(
      "/Access/search-employee-by-userId?userID=" +
      this._configApi.getSpUserEmail()
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async isAccessToProperty(propertyName: string): Promise<boolean> {
    return await this.get(
      `/Access/isAccessToProperty?propertyName=${propertyName}&userId=${this._configApi.getSpUserEmail().split("@")[0]
      }`
    )
      .then((res) => res.data.isAccess)
      .catch(() => undefined);
  }

  public async getGetJobList(
    empKey: string,
    isCanApprove: boolean,
    statusId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<JobItem[]> {
    let url = `/MailJob/GetJobList?empKey=${empKey}&isCanApprove=${isCanApprove}`;
    if (statusId !== undefined) url += `&statusId=${statusId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return await this.get(url)
      .then((res) => {
        if (res.data) {
          return res.data.data;
        }
        return [];
      })
      .catch((error) => {
        console.error(error);
        return [];
      });
  }

  public async getDocumentPreviewFile(mailJobID: number): Promise<any> {
    return await this.get(
      "/MailJob/GetDocumentPreviewFile?mailJobID=" + mailJobID
    )
      .then((res) => res.data)
      .catch((error) => {
        if (error.response) {
          throw error.response.data;
        }
        throw "Network Error";
      });
  }

  public async getMailJobRecipientExelFile(mailJobID: number): Promise<any> {
    return await this.getBlob(
      "/MailJob/GetMailJobRecipientExelFile?mailJobID=" + mailJobID
    )
      .then((res) => res.data)
      .catch((error) => {
        if (error.response) {
          throw error.response.data;
        }
        throw "Network Error";
      });
  }

  public async getAccounts(
    data: GetCommunityData
  ): Promise<Tuple<string, string>[]> {
    return await this.post("/Recipients/GetAccountList", data)
      .then((res) => res.data.accounts)
      .catch(() => []);
  }

  public async getAddress(
    data: GetCommunityData
  ): Promise<Tuple<string, string>[]> {
    return await this.post("Recipients/GetAddressList", data)
      .then((res) => res.data.addresSearch)
      .catch(() => []);
  }

  public async getNames(
    data: GetCommunityData
  ): Promise<Tuple<string, string>[]> {
    return await this.post("/Recipients/GetNameList", data)
      .then((res) => res.data.namesSearch)
      .catch(() => []);
  }

  public async getBlocks(
    data: GetCommunityData
  ): Promise<Tuple<string, string>[]> {
    return await this.post("/Recipients/GetBlockList", data)
      .then((res) => res.data.blocks)
      .catch(() => []);
  }

  public async getGroups(
    data: GetCommunityData
  ): Promise<Tuple<string, string>[]> {
    return await this.post("/Recipients/GetGroupList", data)
      .then((res) => res.data.groups)
      .catch(() => []);
  }

  public async getCommunityListForProperty(
    communityId: number
  ): Promise<CommunityForProperty[]> {
    return await this.get(
      "/Recipients/GetCommunityListForProperty?propertyId=" + communityId
    )
      .then((res) => res.data.community)
      .catch(() => []);
  }

  public async getCommunicationTypeHelpArea(
    comTypeId: number
  ): Promise<CommunicationTypeHelpArea> {
    return await this.get(
      "/Recipients/GetCommunicationTypeHelpArea?helpId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getSelectedDeliveryMethod(
    comTypeId: number
  ): Promise<DeliveryMethod[]> {
    return await this.get(
      "/Recipients/GetSelectedDeliveryMethod?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getSelectedPrintColorMethod(
    comTypeId: number
  ): Promise<PrintColorMethod[]> {
    return await this.get(
      "/Recipients/GetSelectedPrintColorMethod?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getSelectedPrintPreferenceMethod(
    comTypeId: number
  ): Promise<PrintPreference[]> {
    return await this.get(
      "/Recipients/GetSelectedPrintPreferenceMethod?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getSelectedSendOptionMethod(
    comTypeId: number
  ): Promise<SendOption[]> {
    return await this.get(
      "/Recipients/GetSelectedSendOptionMethod?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getScopeDescriptionList(
    comTypeId: number
  ): Promise<ScopeDescription[]> {
    return await this.get(
      "/Recipients/GetScopeDescriptionList?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getDefaultValuesForMessagePage(
    comTypeId: number
  ): Promise<EmailDefault> {
    return await this.get(
      "/Recipients/GetDefaultValuesForMessagePage?comTypeId=" + comTypeId
    )
      .then((res) => res.data)
      .catch(() => undefined);
  }

  public async getSelectedHelpText(
    deliveryMethodName: string
  ): Promise<string> {
    return await this.get(
      "/Recipients/GetSelectedHelpText?additionalOptionName=&deliveryMethodName=" +
      deliveryMethodName
    )
      .then((res) => res.data.item1 + "  " + res.data.item2)
      .catch(() => "");
  }
}
